<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { Form } from 'vee-validate';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { addMixToCart, getNutritionFacts, MixDetails } from '@/api/cart/customizers/mixes';
import { NutritionFacts } from '@/api/productNutritionFacts';
import AddToCartQuantitySelect from '@/components/base/add-to-cart/AddToCartQuantitySelect.vue';
import ArrowIcon from '@/components/base/assets/ArrowIcon.vue';
import CheckIcon from '@/components/base/assets/CheckIcon.vue';
import CloseIcon from '@/components/base/assets/CloseIcon.vue';
import ListIcon from '@/components/base/assets/ListIcon.vue';
import PencilIcon from '@/components/base/assets/PencilIcon.vue';
import PlusIcon from '@/components/base/assets/PlusIcon.vue';
import ShareIcon from '@/components/base/assets/ShareIcon.vue';
import FormInput, { InputAttributes } from '@/components/base/form/FormInput.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Caption from '@/components/base/typography/Caption.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import Header6 from '@/components/base/typography/Header6.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import SelectionsList from '@/components/customizer/mix/SelectionsList.vue';
import ShareModal from '@/components/customizer/mix/ShareModal.vue';
import VariantCard from '@/components/customizer/mix/VariantCard.vue';
import NutritionFactsModal from '@/components/customizer/NutritionFactsModal.vue';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { VolumetricSelection } from '@/lib/customizers/common';
import { MIX_PRODUCT_KEY, MIX_VARIANTS, MixWeight } from '@/lib/customizers/mixes';
import { gtag } from '@/utils/analytics';
import { NutsAddToCartItem, sendProductAddedEvent } from '@/utils/analytics/productAddedEvent';
import { getCartId } from '@/utils/cart';
import { isDefined } from '@/utils/isDefined';
import { Money } from '@/utils/money';
import { getUnitName } from '@/utils/product';

const props = defineProps<{
  selections: (VolumetricSelection | undefined)[];
  total: Money;
}>();

const quantity = defineModel<number>('quantity', { required: true });
const weightSelected = defineModel<MixWeight>('weight', { required: true });

const emit = defineEmits<{
  'add-ingredient': [string];
  'open-modal': [];
  'remove-ingredient': [productKey: string, removeAll?: boolean];
  'show-notification': [string];
}>();

const isMobile = useBreakpoints(breakpointsTailwind).smaller('lg');

const route = useRoute();
const router = useRouter();

const store = useStore();
const { setCart, cart } = useCart(store);

const hideButtonText = ref(true);

const confirmedMixName = ref<string>();
const mixName = ref<string>();
const showNameInput = ref(false);

const inputAttributes: InputAttributes = {
  name: 'mix-name',
  placeholder: 'Name Your Mix',
  type: 'text',
};

const itemName = 'Custom Trail Mix';

const isMixEmpty = computed(() => props.selections.every((ing) => !ing));

const mixDetails = computed<MixDetails>(() => ({
  mixName: confirmedMixName.value,
  quantity: quantity.value,
  selections: props.selections.filter(isDefined),
  weight: weightSelected.value,
}));

const nutritionFacts = ref<NutritionFacts>();
const showNutritionFacts = ref(false);

const showShareOptions = ref(false);

const handleAddIngredient = (productKey: string) => {
  emit('add-ingredient', productKey);
};

const handleRemoveIngredient = (productKey: string, removeAll?: boolean) => {
  emit('remove-ingredient', productKey, removeAll);
};

const handleConfirmName = () => {
  if (mixName.value?.length) {
    confirmedMixName.value = mixName.value;

    if (route.query.name !== mixName.value) {
      router.replace({ query: { ...route.query, name: encodeURIComponent(mixName.value) } });
    }
  }
  showNameInput.value = false;
};

const handleSeeNutritionFacts = useCallback(async () => {
  gtag('event', 'see_mix_nutrition_facts');
  if (!isMixEmpty.value) {
    nutritionFacts.value = await getNutritionFacts(mixDetails.value);
    showNutritionFacts.value = true;
  }
});

const getNutsAddToCartItem = (): NutsAddToCartItem => {
  const price = Money.divide(props.total, quantity.value);
  return {
    cartId: getCartId(cart.value),
    name: itemName,
    pageSection: 'Buy Block',
    price,
    priceBeforeDiscount: price,
    productId: MIX_PRODUCT_KEY,
    quantity: quantity.value,
    reportingCategory: 'Snacks > Trail Mix > Custom Mix',
    sku: `${MIX_PRODUCT_KEY}-${MIX_VARIANTS[weightSelected.value].sku}`,
    variant: getUnitName(MIX_VARIANTS[weightSelected.value]) ?? undefined,
    weight: weightSelected.value,
  };
};

const handleAnalytics = () => {
  const eventProperties = getNutsAddToCartItem();
  sendProductAddedEvent(eventProperties);
};

const handleAddMixToCart = useCallback(async () => {
  if (mixDetails.value?.selections?.filter(isDefined)?.length === 0) {
    emit('show-notification', 'Please add ingredients to your mix first');
    return;
  }

  handleAnalytics();
  const cartAfterAddMix = await addMixToCart(mixDetails.value);
  setCart(cartAfterAddMix);

  await router.push('/cart');
});

onMounted(() => {
  if (route.query.name) {
    mixName.value = decodeURIComponent(route.query.name as string);
    confirmedMixName.value = decodeURIComponent(route.query.name as string);
  }

  if (isMobile.value) {
    setTimeout(() => {
      hideButtonText.value = false;
    }, 75);
    setTimeout(() => {
      hideButtonText.value = true;
    }, 2000);
  }
});

watch(
  () => weightSelected.value,
  () => {
    gtag('event', 'select_mix_weight', {
      weight: weightSelected.value,
    });
  },
);
</script>

<template>
  <div class="flex flex-col gap-4 mx-4 my-6 lg:mx-16 lg:my-7" data-test="custom-mix-buy-block">
    <RouterLink class="flex items-center gap-2 w-fit" to="/custom/mixes">
      <ArrowIcon class="text-black" data-test="back-arrow" direction="left" :size="16" />
      <SmallBodyText class="text-black" data-test="back-to-product-details"
        >Back to Product Details</SmallBodyText
      >
    </RouterLink>
    <div class="flex items-center justify-between h-8 lg:h-10">
      <Form v-if="showNameInput" @submit="handleConfirmName" class="flex items-center gap-4">
        <FormInput
          v-model="mixName"
          dataTest="custom-mix-name"
          :inputAttributes="inputAttributes"
          showLabel
        />
        <UnstyledButton
          @click="showNameInput = false"
          aria-label="Cancel"
          data-test="custom-name-cancel"
          class="flex items-center"
        >
          <CloseIcon />
        </UnstyledButton>
        <UnstyledButton
          aria-label="Save"
          class="flex items-center"
          type="submit"
          data-test="custom-name-submit"
        >
          <CheckIcon />
        </UnstyledButton>
      </Form>
      <div v-else class="flex items-center gap-4" data-test="custom-mix-header">
        <Header4>{{ confirmedMixName ?? itemName }}</Header4>
        <UnstyledButton
          @click="showNameInput = true"
          aria-label="Set custom name"
          data-test="custom-name-edit"
          class="flex items-center"
        >
          <PencilIcon class="cursor-pointer" :size="20" />
        </UnstyledButton>
      </div>
      <UnstyledButton
        @click="$emit('open-modal')"
        aria-label="Add ingredients"
        class="flex items-center gap-2 p-2 text-white bg-black rounded-full lg:hidden whitespace-nowrap btn-shrinking hover:bg-nuts-neutral-500"
        :class="hideButtonText ? 'justify-center btn-small' : 'justify-start btn-large'"
      >
        <PlusIcon :size="20" />
        <SmallBodyText v-if="!hideButtonText" class="mr-2">Add Ingredients</SmallBodyText>
      </UnstyledButton>
    </div>
    <SelectionsList
      :selections="selections"
      @add-ingredient="handleAddIngredient"
      @remove-ingredient="handleRemoveIngredient"
    />
    <Caption class="text-neutral-500" data-test="buyblock-caption">
      *Ratios are by volume, not weight. Due to the personal customization of these mixes, we cannot
      accept returns or issue refunds on these items. Not individually packaged
    </Caption>
    <div class="flex gap-2" role="radiogroup">
      <legend class="sr-only">Size</legend>
      <VariantCard v-model="weightSelected" class="w-1/2" :value="5">
        <div class="flex flex-col justify-center w-full">
          <Header6 headerTag="p">Large bag</Header6>
          <SmallBodyText class="font-normal">5 lbs</SmallBodyText>
        </div>
      </VariantCard>
      <VariantCard v-model="weightSelected" class="w-1/2" :value="25">
        <div class="flex flex-col justify-center w-full">
          <Header6 headerTag="p">Case</Header6>
          <SmallBodyText class="font-normal whitespace-nowrap">25 lbs</SmallBodyText>
        </div>
        <span
          class="self-start px-2 py-1 text-xs text-black bg-nuts-neutral-200 top-1 right-1 rounded-2xl whitespace-nowrap"
        >
          Save 20%
        </span>
      </VariantCard>
    </div>
    <AddToCartQuantitySelect
      v-model="quantity"
      :disabled="false"
      :isLoading="handleAddMixToCart.isPending"
      :totalCost="total"
      @submit="handleAddMixToCart.execute"
    />
    <div class="flex flex-wrap justify-center gap-4">
      <UnstyledButton
        class="flex items-center gap-1"
        data-test="share-links"
        @click="showShareOptions = true"
      >
        <ShareIcon :size="16" />
        <BaseBodyText class="underline">Share This Mix</BaseBodyText>
      </UnstyledButton>
      <UnstyledButton
        class="flex items-center gap-1"
        data-test="nutrition-facts"
        :disabled="handleSeeNutritionFacts.isPending || isMixEmpty"
        @click="handleSeeNutritionFacts.execute"
      >
        <img
          v-if="handleSeeNutritionFacts.isPending"
          class="w-3 h-3 mr-1 animate-spin spinner"
          src="@/assets/spinner.svg"
          alt="spinner"
        />
        <ListIcon v-else :size="16" />
        <BaseBodyText class="underline">See Nutrition Facts</BaseBodyText>
      </UnstyledButton>
    </div>
    <NutritionFactsModal
      :nutritionFacts="nutritionFacts"
      :showModal="showNutritionFacts"
      @close="showNutritionFacts = false"
    />
    <ShareModal :showModal="showShareOptions" @close="showShareOptions = false" />
  </div>
</template>

<style lang="scss" scoped>
.btn-shrinking {
  transition: max-width 0.5s;
  width: 100%;
}
.btn-large {
  max-width: 140px;
}

.btn-small {
  max-width: 36px;
}

img.spinner {
  filter: invert(1);
}
</style>
